import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NeedsAttentionItem, NeedsAttentionSection } from '../../hooks/useKbReadiness';

interface NeedsAttentionQueueProps {
  items: NeedsAttentionItem[];
  loading: boolean;
  onNavigate: (section: NeedsAttentionSection, answersFilter?: 'objections' | 'faqs') => void;
}

const VISIBLE_DEFAULT = 5;

/**
 * Short, prioritized list of the handful of things worth an admin's
 * attention right now. Deliberately caps what's shown by default — a wall
 * of every possible improvement would defeat the point of a calm overview.
 */
const NeedsAttentionQueue: React.FC<NeedsAttentionQueueProps> = ({ items, loading, onNavigate }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-14 rounded-lg border border-hairline bg-surface-subtle animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const visible = showAll ? items : items.slice(0, VISIBLE_DEFAULT);

  const handleClick = (item: NeedsAttentionItem) => {
    if (item.href) {
      if (item.href.startsWith('/knowledge-base')) {
        navigate(item.href);
      } else {
        navigate(item.href);
      }
      return;
    }
    onNavigate(item.section, item.answersFilter);
  };

  return (
    <div className="kb-attention-list space-y-2">
      {visible.map((item) => (
        <div
          key={item.id}
          className={`kb-attention-item flex items-center justify-between gap-4 rounded-lg border p-3.5 ${item.kind === 'required' ? 'is-required border-warning bg-warning-tint' : 'border-hairline bg-surface'}`}
        >
          <div>
            <p className="text-sm text-ink">{item.title}</p>
            <p className="mt-1 text-xs text-ink-muted">
              {item.attentionType === 'live_guidance'
                ? 'Live guidance issue'
                : item.kind === 'required' ? 'Coverage suggestion, required before publication' : 'Coverage suggestion'}
            </p>
          </div>
          <button
            onClick={() => handleClick(item)}
            className="kb-button kb-button-secondary flex-shrink-0 min-h-[40px] px-3 text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-signature"
          >
            {item.ctaLabel}
          </button>
        </div>
      ))}
      {items.length > VISIBLE_DEFAULT && (
        <button onClick={() => setShowAll((v) => !v)} className="min-h-[44px] px-1 text-sm text-signature hover:text-signature-strong focus:outline-none focus:ring-2 focus:ring-signature">
          {showAll ? 'Show fewer' : `View all (${items.length})`}
        </button>
      )}
    </div>
  );
};

export default NeedsAttentionQueue;
